/**
 * Auto-generated activity descriptions.
 *
 * All templates for automatically created descriptions live here for easy
 * editing and debugging. Used by Itinerary, ItineraryDayView, and other
 * components that need consistent description text.
 *
 * Exports:
 *   formatOvernightArrivalDescription(activity) - For overnight arrival rows
 *
 * Add new auto-description formatters as additional exports in this file.
 */

/** Extract country from a comma-separated address (typically last segment). */
function countryFromAddress(address) {
  if (!address || typeof address !== 'string') return '';
  const parts = address.split(',').map((p) => p.trim()).filter(Boolean);
  return parts.length >= 2 ? parts[parts.length - 1] : '';
}

/** Format "City, Country" for display, using city and address for country extraction. */
function formatCityCountry(city, address) {
  const c = String(city || '').trim();
  const country = countryFromAddress(address);
  if (c && country) return `${c}${c !== country ? `, ${country}` : ''}`;
  if (country) return country;
  if (c) return c;
  return '';
}

/**
 * Get the first and last leg/segment of a travel activity for departure/destination.
 * Returns object with first and last endpoints (from_city, from_address, to_city, to_address).
 */
function getTravelEndpoints(activity) {
  const legs = Array.isArray(activity?.legs) ? [...activity.legs] : [];
  legs.sort((a, b) => (Number(a?.leg_index) || 0) - (Number(b?.leg_index) || 0));

  if (legs.length) {
    const first = legs[0] || {};
    const last = legs[legs.length - 1] || {};
    return {
      first: {
        from_city: first.from_city || activity.from_city,
        from_address: first.from_address || activity.from_address,
      },
      last: {
        to_city: last.to_city || activity.to_city,
        to_address: last.to_address || activity.to_address,
      },
    };
  }

  return {
    first: {
      from_city: activity?.from_city,
      from_address: activity?.from_address,
    },
    last: {
      from_city: activity?.from_city,
      from_address: activity?.from_address,
      to_city: activity?.to_city,
      to_address: activity?.to_address,
    },
  };
}

/** Format a human-readable city name (title-case) from a possibly normalized key. */
function formatCityForDisplay(cityKey) {
  const s = String(cityKey || '').trim();
  if (!s) return '';
  return s
    .split(/[\s-_]+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

/**
 * Overnight arrival description: "Arrive to {destination} from {departure}".
 * Uses the last leg's destination and departure; for single-segment trips, uses activity from/to.
 */
export function formatOvernightArrivalDescription(activity) {
  const { last } = getTravelEndpoints(activity);
  // Departure = last leg's FROM (where you departed from for the final segment)
  // Destination = last leg's TO (where you're arriving)
  const depCity = formatCityForDisplay(last.from_city) || last.from_city;
  const depAddr = last.from_address;
  const destCity = formatCityForDisplay(last.to_city) || last.to_city;
  const destAddr = last.to_address;

  const departure = formatCityCountry(depCity, depAddr);
  const destination = formatCityCountry(destCity, destAddr);

  if (!departure && !destination) return '';

  if (!destination) return `Arrive from ${departure}`;
  if (!departure) return `Arrive to ${destination}`;

  return `Arrive to ${destination} from ${departure}`;
}
